import { Input, Chip, Typography } from '@mui/material';
import DatePicker from 'react-datepicker';
import PropTypes from 'prop-types';
import 'react-datepicker/dist/react-datepicker.css';
import {
  ComboBox,
  ComboBoxAudience,
  ComboBoxCreatableExpert,
  ComboBoxCreatableOutlet,
  ComboBoxObjInput,
  MultiSelect,
  FreeSolo,
  UploadCell,
  SingleSelectLinkedService,
  ComboBoxCreatableDepartment,
  ComboBoxCreatableJournalist,
  ComboBoxCreatableCommunityMember,
} from './CellTypes';
import {
  ContentLogKeys,
  DisplayKeys,
  EngagementKeys,
  IssueAndServiceSharedValues,
  IssueKeys,
  ServiceKeys,
} from '../../shared/constants/IssueAndServiceKeys';
import ErrorBoundary from '../ErrorBoundary';
import {
  ContactMethodName,
  TrackerDataType,
  complexityOptions,
  mediaToneOptions,
} from '../../shared/constants/constants';
import RichTextEditor from '../RichTextEditor/RichTextEditor';
import { AddOnTypes } from '../../shared/constants/TenantFields';
import { useSelector } from 'react-redux';
import sxPropType from '../../shared/propTypes/sxPropType';

export default function CellSelector(props) {
  const {
    id,
    cells,
    col,
    className,
    required,
    error,
    dropdownTable,
    classes,
    inputSx,
    labelSx,
    callBack,
    type,
    handleDrag,
    handleDrop,
    isDragActive,
    onEditItem,
    isExpandedRow = false,
    commsMaterialActions,
  } = props;

  // TODO: remove the `classes` prop once the remaining makeStyles-based cell components are migrated.
  const user = useSelector((state) => state.user);
  const userTenantHasEngagementAddOn = user.addonControls[AddOnTypes.engagementLog];

  let options = [];
  let cell = (
    <Input
      align="left"
      className={classes.input}
      sx={inputSx}
      defaultValue={cells[col]}
      onChange={(e) => callBack(e.target.value, col)}
      label={DisplayKeys[col] || col}
    />
  );

  //TODO change to use proper keys after backend flattening removed
  switch (col) {
    case IssueKeys.contactMethod:
      cell = (
        <ComboBox
          options={dropdownTable['contactMethod']}
          col={col}
          callback={callBack}
          val={cells[col]}
          data-cy="contact-select-CellSelector"
          displayLabel={false}
          getOptionLabel={(option) => {
            const name = option?.name ?? '';
            if (name === ContactMethodName.NO_INTERMEDIARY) {
              return 'in person';
            }
            if (name === ContactMethodName.PHONE) {
              return 'phone call';
            }
            return name;
          }}
        />
      );
      break;
    case IssueAndServiceSharedValues.entryDate:
      cell = (
        <DatePicker
          selected={new Date(cells[col] || '')}
          onChange={(date) => {
            callBack(date.toISOString(), col);
          }}
          data-cy="datePicker-CellSelector-entry-date"
        />
      );
      break;
    case ServiceKeys.dueDate:
      cell = (
        <DatePicker
          selected={cells[col] ? new Date(cells[col]) : null}
          onChange={(date) => {
            callBack(date.toISOString(), col);
          }}
          data-cy="datePicker-CellSelector-service-publish-date"
        />
      );
      break;
    case IssueKeys.dueDate:
      cell = (
        <DatePicker
          selected={cells[col] ? new Date(cells[col]) : null}
          onChange={(date) => {
            callBack(date.toISOString(), col);
          }}
          data-cy="datePicker-CellSelector-issue-due-date"
        />
      );
      break;
    case IssueKeys.topic:
      cell = (
        <label htmlFor={DisplayKeys[col] || col} className={classes.label} style={labelSx}>
          {DisplayKeys[col] || col}
          <FreeSolo
            list={dropdownTable['topic']}
            callback={callBack}
            col={col}
            value={cells[col]}
            data-cy="topic-input-CellSelector"
          />
        </label>
      );
      break;
    case IssueAndServiceSharedValues.department:
      cell = (
        <ComboBoxCreatableDepartment
          options={dropdownTable['dept'].filter((dept) => cells.Department === dept.name || !dept.archived)}
          col={col}
          callback={callBack}
          val={cells[col]}
          data-cy="department-select-CellSelector"
        />
      );
      break;
    case IssueAndServiceSharedValues.campaign:
      cell = (
        <MultiSelect
          options={dropdownTable['campaigns'].filter(
            (campaign) =>
              cells[col].map((cellCampaigns) => cellCampaigns.name).includes(campaign.name) || !campaign.archived
          )}
          col={col}
          callback={callBack}
          val={cells[col]}
          data-cy="campaign-select-CellSelector"
        />
      );
      break;
    case IssueAndServiceSharedValues.status:
      cell = (
        <ComboBox
          options={dropdownTable['status']}
          col={col}
          callback={(value) => {
            callBack(value, col);
          }}
          val={cells[col]}
          data-cy="status-select-CellSelector"
          autoAssignFirstOptionIfEmpty={true}
          renderOption={(props, option) => (
            <li {...props}>
              <Chip
                label={(option.name || option) === 'Participation Ribbon' ? 'Participation' : option.name || option}
                variant="filled"
              />
            </li>
          )}
          renderInput={(params) => <Input {...params} label={DisplayKeys[col] || col} variant="outlined" fullWidth />}
        />
      );
      break;
    case IssueAndServiceSharedValues.unit:
      cell = (
        <MultiSelect
          options={dropdownTable['units']
            .filter((unit) => cells[col].map((cellUnits) => cellUnits).includes(unit.name) || !unit.archived)
            .map((unit) => unit.name)}
          col={col}
          callback={callBack}
          val={cells[col]}
          data-cy="unit-select-CellSelector"
        />
      );
      break;
    case 'Expert Required':
    case 'expert':
    case 'unitLead':
    case IssueAndServiceSharedValues.leadExpert:
      cell = (
        <ComboBoxCreatableExpert
          units={dropdownTable['units']}
          options={dropdownTable['expert'].filter(
            (expert) => cells[col].map((cellUnit) => cellUnit._id).includes(expert._id) || !expert.archived
          )}
          col={col}
          callback={callBack}
          val={cells[col]}
          setVal={(v) => (cells[col] = v)}
          className={className}
          required={required}
          error={error}
          data-cy="lead/expert-select-CellSelector"
        />
      );
      break;
    case IssueAndServiceSharedValues.teamMembers:
      if (!Array.isArray(cells[col])) {
        cell = <ComboBoxObjInput options={dropdownTable['lead']} col={col} callback={callBack} val={cells[col]} />;
      } else {
        if (cells[col] && cells[col].length && typeof cells[col][0] === 'string') {
          cells[col] = cells[col].map((member) => {
            return dropdownTable['lead'].find((lead) => lead._id === member);
          });
        }
        cell = (
          <MultiSelect
            options={dropdownTable['lead']}
            col={col}
            callback={callBack}
            val={cells[col]}
            data-cy="teamMember-select-CellSelector"
          />
        );
      }

      break;
    case IssueKeys.outlet:
      cell = (
        <ComboBoxCreatableOutlet
          options={dropdownTable['outlet'].filter((outlet) => cells[col] === outlet._id || !outlet.archived)}
          col={col}
          callback={callBack}
          val={cells[col]}
          data-cy="outlet-select-CellSelector"
        />
      );
      break;
    case EngagementKeys.organization:
      cell = (
        <ComboBoxCreatableOutlet
          options={dropdownTable['organization'].filter(
            (organization) => cells[col] === organization._id || !organization.archived
          )}
          col={col}
          callback={callBack}
          val={cells[col]}
          data-cy="organization-select-CellSelector"
        />
      );
      break;
    case IssueKeys.journalist:
      if (cells[IssueKeys.outlet]) {
        options = dropdownTable['orgAgents'][cells[IssueKeys.outlet]]?.filter(
          (journalist) => journalist.Org === cells[IssueKeys.outlet] || !journalist.archived
        );
      } else {
        options = dropdownTable['journalist'].filter(
          (journalist) => cells[col] === journalist._id || !journalist.archived
        );
      }

      cell = (
        <ComboBoxCreatableJournalist
          orgs={dropdownTable['outlet']}
          options={options}
          col={col}
          callback={callBack}
          val={cells[col]}
          selectedOrg={cells[IssueKeys.outlet]}
          data-cy="journalist-select-CellSelector"
        />
      );
      break;
    case EngagementKeys.communityMember:
      if (cells[EngagementKeys.organization]) {
        options = dropdownTable['organizationAgents'][cells[EngagementKeys.organization]]?.filter(
          (communityMember) =>
            communityMember.Organization === cells[EngagementKeys.organization] || !communityMember.archived
        );
      } else {
        options = dropdownTable['communityMember'].filter(
          (communityMember) => cells[col] === communityMember._id || !communityMember.archived
        );
      }

      cell = (
        <ComboBoxCreatableCommunityMember
          orgs={dropdownTable['organization']}
          options={options}
          col={col}
          callback={callBack}
          val={cells[col]}
          selectedOrg={cells[EngagementKeys.organization]}
          data-cy="communityMember-select-CellSelector"
        />
      );
      break;
    case ContentLogKeys.audience:
      cell = (
        <ComboBoxAudience
          options={dropdownTable['audience'].filter(
            (audience) =>
              (Array.isArray(cells[col]) ? cells[col] : [cells[col]]).some(
                (selectedAudience) => (selectedAudience?._id ?? selectedAudience) === audience._id
              ) || !audience.archived
          )}
          col={col}
          callback={callBack}
          val={cells[col]}
          data-cy="audience-select-CellSelector"
        />
      );
      break;
    case ContentLogKeys.contentType:
      cell = (
        <ComboBox
          options={(dropdownTable['contentType'] || []).filter(
            (contentType) =>
              [Array.isArray(cells[col]) ? cells[col][0] : cells[col]].some(
                (selectedContentType) =>
                  (selectedContentType?._id ?? selectedContentType?.name ?? selectedContentType) ===
                  (contentType._id ?? contentType.name)
              ) || contentType.archived !== true
          )}
          col={col}
          callback={callBack}
          val={
            (Array.isArray(cells[col]) ? cells[col][0] : cells[col])?._id ??
            (Array.isArray(cells[col]) ? cells[col][0] : cells[col])?.name ??
            (Array.isArray(cells[col]) ? cells[col][0] : cells[col])
          }
          renderSingleSelectColorChip
          data-cy="contentType-select-CellSelector"
        />
      );
      break;
    case EngagementKeys.notes:
    case ContentLogKeys.content:
    case ContentLogKeys.otherNotes:
    case IssueAndServiceSharedValues.keyMessaging:
      cell = (
        <RichTextEditor
          defaultValue={''}
          value={cells[col]}
          onChange={(value) => {
            callBack(value, col);
          }}
          dataCy={'keyMessaging-input-CellSelector'}
          readOnly={false}
        />
      );
      break;
    case EngagementKeys.background:
    case ContentLogKeys.background:
    case IssueAndServiceSharedValues.backgroundResponse:
      cell = (
        <RichTextEditor
          defaultValue={''}
          value={cells[col]}
          onChange={(value) => {
            callBack(value, col);
          }}
          dataCy={'backgroundResponse-input-CellSelector'}
          readOnly={false}
        />
      );
      break;
    case IssueAndServiceSharedValues.type:
      cell = (
        <MultiSelect
          options={dropdownTable['type'].filter(
            (type) => cells[col].map((cellTypes) => cellTypes.name).includes(type.name) || !type.archived
          )}
          col={col}
          callback={callBack}
          val={cells[col]}
          groupBy={(option) => option.type}
          data-cy="type-select-CellSelector"
        />
      );
      break;

    case IssueKeys.linkedService:
    case ContentLogKeys.linkedService:
      cell = (
        <SingleSelectLinkedService
          options={dropdownTable['service']}
          col={col}
          callback={callBack}
          val={cells[col] && cells[col].length > 0 ? cells[col][0] : ''}
          data-cy="linkedService-select-CellSelector"
        />
      );
      break;
    // case 'Collaborator Access':
    //   cell = <ComboBox options={dropdownTable['collaboratorAccess']} col={col} callback={callBack} val={cells[col]} />;
    //   break;
    case ServiceKeys.complexity:
      cell = (
        <ComboBox
          options={complexityOptions}
          col={col}
          callback={callBack}
          val={cells[col]}
          data-cy="complexity-slect-CellSelector"
          isConstant
        />
      );
      break;
    // temporarily hidden
    // case ServiceKeys.diversity:
    //   cell = (
    //     <ComboBox
    //       options={diversityOptions}
    //       col={col}
    //       callback={callBack}
    //       val={cells[col]}
    //       required={required}
    //       error={error}
    //       data-cy="diversity-comboBox-CellSelector"
    //       isConstant
    //     />
    //   );
    //   break;
    case ServiceKeys.linkedInteraction:
      cell = (
        <div>
          {userTenantHasEngagementAddOn ? (
            <Typography>
              Link existing Media Interactions or Engagements to this service via their corresponding pages
            </Typography>
          ) : (
            <Typography>Link existing Media Interactions to this service via the Media Interaction page</Typography>
          )}
        </div>
      );
      break;
    case ServiceKeys.linkedEngagement:
      cell = (
        <div>
          <Typography>Link existing Engagements to this service via the Engagements page</Typography>
          {/* <CellViewer cells={cells} index={col} dropdownTable={dropdownTable} /> */}
        </div>
      );
      break;
    case IssueKeys.file:
      cell = (
        <UploadCell
          id={id}
          cellName={col}
          files={cells[col]}
          callback={callBack}
          type={type}
          data-cy="storyLink-upload-CellSelector"
          onEditItem={onEditItem}
          dropzoneStyleType={isExpandedRow ? type : undefined}
          emptyDropzoneActions={commsMaterialActions}
          hideEmptyDropzone={isExpandedRow && type === TrackerDataType.MEDIA_INTERACTION}
          compactCardGrid={isExpandedRow}
        />
      );
      break;
    case ServiceKeys.file:
      cell = (
        <UploadCell
          id={id}
          cellName={col}
          files={cells[col]}
          callback={callBack}
          type={type}
          data-cy="commsMaterial-upload-CellSelector"
          handleDrag={handleDrag}
          handleDrop={handleDrop}
          isDragActive={isDragActive}
          onEditItem={onEditItem}
          dropzoneStyleType={isExpandedRow ? type : undefined}
          emptyDropzoneActions={commsMaterialActions}
          compactCardGrid={isExpandedRow}
        />
      );
      break;
    case EngagementKeys.file:
    case ContentLogKeys.file:
      cell = (
        <UploadCell
          id={id}
          cellName={col}
          files={cells[col]}
          callback={callBack}
          type={type}
          data-cy="relevantDocuments-upload-CellSelector"
          handleDrag={handleDrag}
          handleDrop={handleDrop}
          isDragActive={isDragActive}
          onEditItem={onEditItem}
          dropzoneStyleType={isExpandedRow ? type : undefined}
          emptyDropzoneActions={commsMaterialActions}
          compactCardGrid={isExpandedRow}
        />
      );
      break;
    case ContentLogKeys.liveLink:
      cell = (
        <UploadCell
          id={id}
          cellName={col}
          files={cells[col] ? [{ ...cells[col], type: 'url' }] : []}
          callback={(updatedFiles) => callBack(updatedFiles?.[0] ?? null, col)}
          type={type}
          handleDrag={handleDrag}
          handleDrop={handleDrop}
          isDragActive={isDragActive}
          onEditItem={onEditItem}
          popupType={type}
          dropzoneStyleType={isExpandedRow ? type : undefined}
          emptyDropzoneActions={commsMaterialActions}
          compactCardGrid={isExpandedRow}
        />
      );
      break;
    case IssueKeys.tone:
      cell = (
        <ComboBox
          options={mediaToneOptions}
          col={col}
          callback={callBack}
          val={cells[col]}
          required={required}
          error={error}
          data-cy="tone-comboBox-CellSelector"
          isConstant
        />
      );
      break;
    default:
      //TODO copy default case from queryAdder once issue flattening is fixed
      break;
  }

  return <ErrorBoundary>{cell}</ErrorBoundary>;
}

CellSelector.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  cells: PropTypes.object.isRequired,
  col: PropTypes.string.isRequired,
  className: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.bool,
  dropdownTable: PropTypes.shape({
    contactMethod: PropTypes.array,
    topic: PropTypes.array,
    dept: PropTypes.array,
    campaigns: PropTypes.array,
    status: PropTypes.array,
    units: PropTypes.array,
    expert: PropTypes.array,
    lead: PropTypes.array,
    outlet: PropTypes.array,
    organization: PropTypes.array,
    orgAgents: PropTypes.object,
    journalist: PropTypes.array,
    organizationAgents: PropTypes.object,
    communityMember: PropTypes.array,
    audience: PropTypes.array,
    contentType: PropTypes.array,
    type: PropTypes.array,
    service: PropTypes.array,
  }).isRequired,
  classes: PropTypes.shape({
    input: PropTypes.string,
    label: PropTypes.string,
  }).isRequired,
  callBack: PropTypes.func.isRequired,
  type: PropTypes.string,
  handleDrag: PropTypes.func,
  handleDrop: PropTypes.func,
  isDragActive: PropTypes.bool,
  onEditItem: PropTypes.func,
  isExpandedRow: PropTypes.bool,
  commsMaterialActions: PropTypes.node,
  inputSx: sxPropType,
  labelSx: sxPropType,
};
